import { motion } from 'framer-motion';
import { isEqual, round } from 'lodash';
import { Compass } from 'lucide-react';
import pluralize from 'pluralize';

import { Heading } from '@/components/heading';
import { Icon } from '@/components/icon';
import { Text } from '@/components/text';
import { UserMessage } from '@/components/ui/user-message';
import { VisuallyHidden } from '@/components/visually-hidden';
import { DistanceUnitsType, LocationInterface, PortInterface, PortsFiltersInterface } from '@/types';
import { getPreferredIdentifier } from '@/utils/helpers';
import { getTransportColor } from '@/utils/helpers/get-transport-color';
import { tv } from '@/utils/styles';

import { getDistanceFromSearchOrigin, getDistanceInSelectedUnits } from '../map/utils';
import { PortsCountBox } from './count-box';

const PortsList = ({
  filteredPorts,
  handleItemClick,
  handleItemHover,
  hoveredPort,
  searchOrigin,
  searchRadius,
  handleNearbyListingClose,
  currentTransportType,
  distanceUnits,
}: {
  filteredPorts: PortInterface[];
  handleItemClick: (port: PortInterface) => void;
  handleItemHover: (port: PortInterface | null) => void;
  hoveredPort: PortInterface | null;
  searchOrigin: LocationInterface;
  searchRadius: string;
  handleNearbyListingClose?: () => void;
  currentTransportType: PortsFiltersInterface['transportType'];
  distanceUnits: DistanceUnitsType;
}) => {
  const { heading, listWrapper, list, whiteFadeOut, button, identifierId, distanceFromSearchOrigin, closeButton } =
    portsListStyles();
  return (
    <>
      <motion.div className={heading()}>
        <button onClick={() => handleNearbyListingClose?.()} className={closeButton()} type="button">
          <VisuallyHidden>Close ports list</VisuallyHidden>
          <Icon name="close" size="md" color="white" />
        </button>
        <div className="max-w-[calc(100%-100px)]">
          <Heading variant="h4" className="mb-0 flex items-start">
            <Compass size={22} className="mr-1.5 shrink-0 text-lightBlue-600" /> Ports near {searchOrigin?.name}
          </Heading>
        </div>
        <div>
          <PortsCountBox filteredPorts={filteredPorts} />
        </div>
      </motion.div>

      {filteredPorts.length > 0 ? (
        <div className={listWrapper()}>
          <ul className={list()}>
            {filteredPorts.map((port: PortInterface) => {
              const distanceFromSearchOriginValue = getDistanceFromSearchOrigin(port, searchOrigin);
              const preferredIdentifier = getPreferredIdentifier(port.ids);

              return (
                <li key={`${port.name}-${port.id}`}>
                  <button
                    onClick={() => handleItemClick(port)}
                    onMouseEnter={() => handleItemHover(port)}
                    onMouseLeave={() => handleItemHover(null)}
                    type="button"
                    className={button({ isActive: isEqual(port, hoveredPort) })}
                    data-cy="port-insights-ports-list-item"
                  >
                    <div className="flex w-full">
                      <Text
                        variant="sm"
                        className="mb-2 font-semibold uppercase"
                        style={{
                          color: getTransportColor(port.type),
                        }}
                      >
                        {port.type}
                      </Text>
                    </div>
                    <Heading as="h3" variant="h5" className="mb-base">
                      {port.name}
                    </Heading>
                    <p className={identifierId()}>
                      {preferredIdentifier?.type}: <b>{preferredIdentifier?.value}</b>
                    </p>
                    <div className="mt-auto flex w-full justify-between">
                      <div className={distanceFromSearchOrigin()}>
                        <Icon name="map-marker-radius" size="base" color="grey.700" className="mr-xs" />
                        <Text
                          as="span"
                          variant="label"
                          className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
                        >{`${round(
                          getDistanceInSelectedUnits(distanceFromSearchOriginValue, distanceUnits),
                          1,
                        )} ${distanceUnits}`}</Text>
                      </div>
                      <Text variant="label" as="span" className="text-grey-400">
                        {port.carrierCount} {pluralize('carrier', port.carrierCount)}
                      </Text>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          <span className={whiteFadeOut()} />
        </div>
      ) : (
        <UserMessage
          heading={`There are no ${
            currentTransportType === 'all' ? 'ports' : `${currentTransportType}s`
          } within ${searchRadius} ${distanceUnits} of that location`}
          body="Try expanding the search radius or changing filters to see more results."
          variant="warning"
          className="!mb-0 mt-4"
        />
      )}
    </>
  );
};

export const portsListStyles = tv({
  slots: {
    heading: 'flex w-full items-center justify-between max-sm:pr-5',
    listWrapper:
      'relative -mb-2 -ml-1 mt-3 h-full w-[calc(100%+theme(spacing.4)-1px)] translate-x-[-3px] overflow-hidden 2xl:-ml-2 2xl:-mr-2 2xl:mt-4 2xl:w-[calc(100%+theme(spacing.6))]',
    list: 'scrollbar grid h-full gap-3 gap-y-2 overflow-y-auto pb-4 pl-[3px] pr-2 2xl:grid-cols-2 2xl:gap-y-3',
    button:
      'flex h-full w-full flex-col justify-between rounded-md border border-solid border-grey-200 bg-white p-3 text-left transition-all duration-200 ease-in-out hover:border-grey-300 hover:shadow-card 2xl:p-4',
    whiteFadeOut: 'absolute -bottom-px left-0 right-3 h-4 bg-gradient-to-t from-[rgb(250,250,250)] to-transparent',
    identifierId: 'mb-4 text-sm uppercase text-text-secondary [&_b]:font-medium',
    distanceFromSearchOrigin: 'flex items-center text-grey-500',
    closeButton:
      'absolute -right-3 -top-3 grid size-10 cursor-pointer place-items-center rounded-[24px] bg-[#222] opacity-90 hover:opacity-100 sm:-right-[62px] sm:top-2 sm:size-12 [&_svg>*]:fill-white [&_svg]:size-4 sm:[&_svg]:size-6',
  },
  variants: {
    isActive: {
      true: {
        button: 'border-grey-400',
      },
    },
  },
});

export { PortsList };
